import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Route } from "../../routes";
import ErrorsAlert, { combineErrors } from "../ErrorsAlert/ErrorsAlert";
import Input, { EInputType, IInputField, IOption } from "../ui/Input/Input";
import { useInputs } from "../ui/Input/useInputs";
import { getInputData, isInputsValid } from "../ui/Input/input-utils";
import { IPriceQuoteRequest, useBookingService } from "../../services/booking-service";
import Button, { EButtonColor, EButtonSize } from "../ui/Button/Button";
import { ErrorResponse } from "../../custom-fetch";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useStationOptions } from "../../hooks/useStationOptions";
import { useStationAvailability } from "../../hooks/useStationAvailability";
import { addDays, differenceInDays, format, isValid, parseISO, addMonths } from "date-fns";
import { QueryKey } from "../../services/query-keys";
import IPriceQuote, { ICategory } from "../../shared/IPriceQuote";
import { formatCurrency } from "../../utils/currency-util";
import { RefHandle } from "../../shared/RefHandle";
import IExtra from "../../shared/IExtra";
import Extras from "./Extras/Extras";
import Table from "../ui/Table/Table";
import countryOptions from "../../shared/countryOptions";
import IReserve from "../../shared/IReserve";
import IReservation, { IWheelsStation, EReservationStatus } from "../../shared/IReservation";
import { useReservationService } from "../../services/reservations-service";
import { extrasBlacklist, extrasDefaultTrue, meetingPointAirportOptions, meetingPointOptions, terminalOptions, timeOptions } from "../../shared/bookingOptions";
import { EMeetingPoint, EMeetingPointAirport } from "../../shared/bookingEnums";
import PreviousSearches, { ISavedSearch } from "./PreviousSearches/PreviousSearches";
import "./Reserve.scss";

enum EInputsPriceQuote {
  departureDate = "departureDate",
  departureTime = "departureTime",
  departureStation = "departureStation",
  returnDate = "returnDate",
  returnTime = "returnTime",
  returnStation = "returnStation",
  discountCode = "discountCode"
}

enum EInputs {
  customerFirstName = "customerFirstName",
  customerLastName = "customerLastName",
  customerPhone = "customerPhone",
  customerEmail = "customerEmail",
  customerAddress = "customerAddress",
  customerCity = "customerCity",
  customerZip = "customerZip",
  customerCountry = "customerCountry",
  meetingPoint = "meetingPoint",
  meetingPointAirport = "meetingPointAirport",
  meetingPointFlightNumber = "meetingPointFlightNumber",
  meetingPointEta = "meetingPointEta",
  meetingPointTerminal = "meetingPointTerminal",
  adults = "adults",
  childs = "childs",
  infants = "infants",
  notes = "notes",
  refNo = "refNo",
}

const getExtraValueCount = (val: any): number => {
  if (Array.isArray(val)) return val.includes("true") ? 1 : 0;
  if (typeof val === "string") {
    if (val === "true") return 1;
    if (val === "false" || val === "") return 0;
    const parsed = parseInt(val, 10);
    return !isNaN(parsed) ? Math.max(0, parsed) : 0;
  }
  return typeof val === "number" ? Math.max(0, val) : 0;
};

const transformExtraValueForPayload = (val: any, isQuant: boolean): string => {
  const count = getExtraValueCount(val);
  if (isQuant) return count.toString();
  return count > 0 ? "true" : "false";
};

export interface ITabSummary {
  pickup: string;
  dates: string;
}

interface IReserveTabContentProps {
  tabId: string;
  pickupOptions: IOption[] | undefined;
  returnOptions: IOption[] | undefined;
  stationsLoading: boolean;
  stations: IWheelsStation[] | undefined;
  onUpdateSummary: (tabId: string, summary: ITabSummary) => void;
  onUpdateSearchState: (tabId: string, data: IPriceQuoteRequest) => void;
  onSearchExecuted: (tabId: string, data: IPriceQuoteRequest) => void;
  onBookingSuccess: (refNo: string, irn?: string) => void;
  initialData?: IPriceQuoteRequest;
  externalData?: { data: IPriceQuoteRequest; triggerId: number };
  savedSearches: ISavedSearch[];
  onSelectSearch: (search: ISavedSearch) => void;
  onOpenInNewTab: (search: ISavedSearch) => void;
  onDeleteSearch: (id: string) => void;
  onClearAll: () => void;
}

const ReserveTabContent: React.FC<IReserveTabContentProps> = ({ 
  tabId,
  pickupOptions, 
  returnOptions, 
  stationsLoading, 
  stations,
  onUpdateSummary,
  onUpdateSearchState,
  onSearchExecuted,
  onBookingSuccess,
  initialData,
  externalData,
  savedSearches,
  onSelectSearch,
  onOpenInNewTab,
  onDeleteSearch,
  onClearAll,
}) => {
  const MIN_BOOKING_DAYS = 1;
  const MIN_DAYS_TO_START = 3;

  const { validateRefNo } = useReservationService();

  const [refNoMessage, setRefNoMessage] = useState<string | null>(null);
  const [isValidatingRefNo, setIsValidatingRefNo] = useState(false);
  const previousRefNo = useRef<string | null>(null);

  const departureMinDate = addDays(new Date(), MIN_DAYS_TO_START);
  const returnMinDate = addDays(departureMinDate, MIN_BOOKING_DAYS);
  const formattedDepartureMin = format(departureMinDate, 'yyyy-MM-dd');
  const formattedReturnMin = format(returnMinDate, 'yyyy-MM-dd');

  const { 
    createInput: createInputSearch, 
    submit: submitSearch, 
    inputs: searchInputs,
    setFormValues: setSearchFormValues
  } = usePriceQuoteInputs(formattedDepartureMin, formattedReturnMin, returnOptions, initialData);

  useEffect(() => {
    if (externalData?.data) {
      setSearchFormValues(externalData.data);
      setPriceQuote(undefined);
      setVehicle(undefined);
      setExtras([]);
      lastSearchData.current = undefined;
    }
  }, [externalData?.triggerId, externalData?.data, setSearchFormValues]);

  useEffect(() => {
    const data: IPriceQuoteRequest = {
      departureDate: searchInputs[EInputsPriceQuote.departureDate].value as string,
      departureTime: searchInputs[EInputsPriceQuote.departureTime].value as string,
      departureStation: searchInputs[EInputsPriceQuote.departureStation].value as string,
      returnDate: searchInputs[EInputsPriceQuote.returnDate].value as string,
      returnTime: searchInputs[EInputsPriceQuote.returnTime].value as string,
      returnStation: searchInputs[EInputsPriceQuote.returnStation].value as string,
      discountCode: (searchInputs[EInputsPriceQuote.discountCode]?.value as string) || "",
    };
    onUpdateSearchState(tabId, data);
  }, [searchInputs, onUpdateSearchState, tabId]);

  const departureDateValue = searchInputs[EInputsPriceQuote.departureDate].value as string;
  const returnDateValue = searchInputs[EInputsPriceQuote.returnDate].value as string;

  let durationString = "-";
  if (departureDateValue && returnDateValue) {
    const depDate = parseISO(departureDateValue);
    const retDate = parseISO(returnDateValue);
    const durationCount = differenceInDays(retDate, depDate) + 1;
    if (durationCount > 0) {
      durationString = `${durationCount}`;
    }
  }

  const { createInput, submit, inputs } = useDriverDetailsInputs();
  const [ priceQuote, setPriceQuote ] = useState<IPriceQuote>();
  const { getPriceQuote, isPendingPrices, isErrorPrices, errorPrices } = useSearch(setPriceQuote);
  const { saveReservationAsync, isPendingSave, isErrorSave, errorSave } = useSaveReservation();

  const refNo = inputs[EInputs.refNo].value;

  useEffect(() => {
    if (refNo && refNo !== previousRefNo.current) {
      const validate = async () => {
        setIsValidatingRefNo(true);
        try {
          const result = await validateRefNo(refNo as string, previousRefNo.current || "");
          setRefNoMessage(result);
        } catch (err: any) {
          if (err instanceof ErrorResponse) {
            setRefNoMessage(err.messages[0]);
          } else {
            setRefNoMessage("Error validating reference number");
          }
        } finally {
          setIsValidatingRefNo(false);
          previousRefNo.current = refNo as string;
        }
      };

      const timer = setTimeout(validate, 500);
      return () => clearTimeout(timer);
    } else if (!refNo) {
      setRefNoMessage(null);
      previousRefNo.current = null;
    }
  }, [refNo, validateRefNo]);

  const [priceQuoteReq, setPriceQuoteReq] = useState<IPriceQuoteRequest>();

  const [vehicle, setVehicle] = useState<ICategory>();

  const [extras, setExtras] = useState<IExtra[]>([]);
  const extrasRef = useRef<RefHandle<IExtra[]>>(null);

  const getExtraCount = (code: string): number => {
    const extra = extras.find(e => e.code === code);
    return extra ? getExtraValueCount(extra.value) : 0;
  };

  const countBBS = getExtraCount('BBS');
  const countCBS = getExtraCount('CBS');

  const babySeatGroups = [
    { type: "BBS", count: countBBS, label: "Baby Seat" },
    { type: "CBS", count: countCBS, label: "Child Seat" }
  ].filter(group => group.count > 0);

  const [babySeatsData, setBabySeatsData] = useState<Record<string, string>>({});

  const handleInputChangeBabySeats = (key: string, value: string) => {
    let sanitizedValue = value;
    if (value && !isNaN(parseInt(value))) {
      sanitizedValue = Math.max(0, parseInt(value)).toString();
    }
    
    setBabySeatsData(prev => ({
      ...prev,
      [key]: sanitizedValue
    }));
  };

  const { fetchExtras,  } = useBookingService()
  const {
    data: fetchedExtras,
    isPending: isPendingExtras,
    isError: isErrorExtras,
    error: errorExtras,
  } = useQuery<IExtra[], ErrorResponse>({
    queryKey: [QueryKey.extras],
    queryFn: ({ signal }) => fetchExtras({ signal }),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (vehicle && vehicle.options && fetchedExtras) {
      const extraMap = new Map(fetchedExtras.map(e => [e.code, e]));

      const extrasWithRates: IExtra[] = vehicle.options.reduce((acc: IExtra[], option) => {
        const matchingExtra = extraMap.get(option.code);
        if (matchingExtra) {
          acc.push({
            ...matchingExtra,
            rate: option.firstFree ? 0 : option.rate,
            value: extrasDefaultTrue.includes(option.code) ? "true" : "0"
          });
        }
        return acc;
      }, []);

      const filteredExtras = extrasWithRates.filter(extra => {
        if (extrasBlacklist.includes(extra.code)) return false;
        if (extra.code === 'TCC' && priceQuoteReq?.departureStation !== 'MAN') {
          return false;
        }
        return true;
      });

      if (filteredExtras?.length > 0) {
        setExtras(filteredExtras);
      }
    }
  }, [vehicle, fetchedExtras, priceQuoteReq?.departureStation]);

  const lastSearchData = useRef<IPriceQuoteRequest | undefined>(undefined);
  const isInitialMount = useRef<boolean>(true);

  const submitHandlerSearch = useCallback(async (mode: 'auto' | 'manual' = 'manual') => {
    let data: IPriceQuoteRequest | null = null;
    
    if (mode === 'manual') {
      const result = await submitSearch();
      data = result as IPriceQuoteRequest | null;
    } else {
      // Auto mode: silent validation
      if (isInputsValid(searchInputs)) {
        data = getInputData<IPriceQuoteRequest>(searchInputs);
      }
    }

    if (!data) {
      return;
    }

    const searchData = data;

    // Check if data has changed before searching in auto mode
    if (mode === 'auto' && lastSearchData.current && JSON.stringify(lastSearchData.current) === JSON.stringify(searchData)) {
      return;
    }

    const isFirstRun = isInitialMount.current;
    if (isFirstRun) {
      isInitialMount.current = false;
    }

    lastSearchData.current = { ...searchData };
    setPriceQuote(undefined);
    setVehicle(undefined);
    setExtras([]);
    
    const pickupStation = stations?.find(s => s.code === searchData.departureStation);
    const pickupFirstWord = pickupStation?.name.split(' ')[0] || '';
    
    const depDateParsed = parseISO(searchData.departureDate);
    const retDateParsed = parseISO(searchData.returnDate);

    if (!isValid(depDateParsed) || !isValid(retDateParsed)) {
      console.warn("Invalid date(s) provided in search data", { 
        departureDate: searchData.departureDate, 
        returnDate: searchData.returnDate 
      });
      return;
    }

    const depDate = format(depDateParsed, "dd/MM");
    const retDate = format(retDateParsed, "dd/MM");

    onUpdateSummary(tabId, {
      pickup: pickupFirstWord,
      dates: `${depDate} - ${retDate}`
    });

    // Only save to previous search history on manual search OR after initial mount (when user adjusts inputs)
    const shouldSaveSearch = mode === 'manual' || !isFirstRun;
    if (shouldSaveSearch) {
      const savedSearchPayload: IPriceQuoteRequest = {
        departureDate: format(depDateParsed, "yyyy-MM-dd"),
        departureTime: searchData.departureTime,
        departureStation: searchData.departureStation,
        returnDate: format(retDateParsed, "yyyy-MM-dd"),
        returnTime: searchData.returnTime,
        returnStation: searchData.returnStation,
        discountCode: searchData.discountCode || "",
      };
      onSearchExecuted(tabId, savedSearchPayload);
    }

    searchData.departureDate = format(depDateParsed, "dd/MM/yyyy");
    searchData.returnDate = format(retDateParsed, "dd/MM/yyyy");
    
    getPriceQuote(searchData);
    setPriceQuoteReq(searchData);
  }, [submitSearch, searchInputs, getPriceQuote, stations, onUpdateSummary, onSearchExecuted, tabId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      submitHandlerSearch('auto');
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInputs, submitHandlerSearch]);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const submitHandlerReserve = useCallback(async () => {
    setSubmitError(null);
    const data = await submit();
    if (data) {
      console.log(data)
      if (extrasRef.current) {
        const refData = await extrasRef.current.getData();
        if (refData) {
          console.log(refData);

          const selectedExtra = refData.map(item => ({ 
            ...item, 
            value: transformExtraValueForPayload(item.value, !!item.quant) 
          }));

          const reservePayload: IReserve  = {
            ...data,
            ...priceQuoteReq,
            departureCode: priceQuoteReq?.departureStation,
            returnCode: priceQuoteReq?.returnStation,
            selectedCategory: {...vehicle},
            selectedExtra: [...selectedExtra],
            BBSAmount: countBBS,
            CBSAmount: countCBS,
            ...babySeatsData
          };
          console.log(reservePayload)
          if (reservePayload) {
            const departureStation = stations?.find(s => s.code === priceQuoteReq?.departureStation);
            const returnStation = stations?.find(s => s.code === priceQuoteReq?.returnStation);
            
            reservePayload.departureName = departureStation?.name || "";
            reservePayload.returnName = returnStation?.name || "";

            const result = await saveReservationAsync(reservePayload);
            if (result && result.status === EReservationStatus.REJ) {
              setSubmitError("Reservation was rejected and could not be saved.");
              return;
            }
            if (result && result.refNo) {
              onBookingSuccess(result.refNo, result.irn);
            }
          }
        }
      }
    }
    
  }, [babySeatsData, countBBS, countCBS, priceQuoteReq, saveReservationAsync, submit, vehicle, stations, onBookingSuccess]);

  const errorMessages = combineErrors({ isError: isErrorPrices, error: errorPrices }, { isError: isErrorExtras, error: errorExtras });

  interface IBasketItem {
    name: string;
    value: number;
  }
  const shoppingBasket = useMemo(() => {
    const items: IBasketItem[] = [];
    let totalRate = 0;

    if (vehicle) {
      let vehicleRemainder = vehicle.totalRate;

      if (vehicle.oneWayCharge > 0) {
        const value = vehicle.oneWayCharge;
        totalRate += value;
        items.push({ name: "One-way rental charge", value });
        vehicleRemainder -= value;
      }

      if (vehicle.outOfHours > 0) {
        const value = vehicle.outOfHours;
        totalRate += value;
        items.push({ name: "Out of office hours charge", value });
        vehicleRemainder -= value;
      }

      totalRate += (vehicleRemainder);
      const baseCharge = vehicle.optionsByCode?.BSC?.rate || 0;
      if (baseCharge > 0) {
        items.push({ name: "Base Charge", value: baseCharge });
      }
      
      const modelValue = vehicleRemainder - baseCharge;
      items.push({
        name: vehicle.model,
        value: modelValue
      });
    }

    extras.forEach((extra) => {
      const count = getExtraValueCount(extra.value);
      
      if (count > 0) {
        const value = extra.rate && extra.rate > 0 ? (extra.rate * count) : 0;
        totalRate += value;
        items.push({
          name: `${count} ${extra.name ?? ''} ${extra.code}`,
          value
        });
      }
    });

    return { items, totalRate };
  }, [vehicle, extras]);

  return (
    <>
      <div className="reserve-section">
        <div className="flex flex-col xl:flex-row gap-6 items-stretch">
          <div className="flex-1 min-w-0 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {createInputSearch(EInputsPriceQuote.departureDate)}
                  {createInputSearch(EInputsPriceQuote.departureTime)}
                  <div className="sm:col-span-2">
                    {createInputSearch(EInputsPriceQuote.departureStation, { options: pickupOptions, loading: stationsLoading })}
                  </div>
                </div>
              </div>

              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {createInputSearch(EInputsPriceQuote.returnDate)}
                  {createInputSearch(EInputsPriceQuote.returnTime)}
                  <div className="sm:col-span-2">
                    {createInputSearch(EInputsPriceQuote.returnStation, { options: returnOptions, loading: stationsLoading })}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-4 mt-4">
              <div style={{ flex: "1 1 200px", maxWidth: "260px" }}>
                {createInputSearch(EInputsPriceQuote.discountCode, { containerStyles: { marginBottom: "0" } })}
              </div>
              <div style={{ width: "120px" }}>
                <Input
                  type={EInputType.text}
                  inputName="duration"
                  label="Duration"
                  value={durationString !== "-" ? `${durationString} ${durationString === '1' ? 'day' : 'days'}` : "-"}
                  onChange={() => {}}
                  disabled={true}
                  containerStyles={{ marginBottom: "0" }}
                />
              </div>
              <div>
                <Button 
                  size={EButtonSize.SMALL} 
                  onClick={() => submitHandlerSearch('manual')} 
                  loading={isPendingPrices || isPendingExtras} 
                  style={{ minWidth: "10rem", height: "38px" }}
                >
                  Search
                </Button>
              </div>
            </div>
          </div>

          <div className="reserve-searches-sidebar">
            <PreviousSearches 
              searches={savedSearches}
              onSelectSearch={onSelectSearch}
              onOpenInNewTab={onOpenInNewTab}
              onDeleteSearch={onDeleteSearch}
              onClearAll={onClearAll}
            />
          </div>
        </div>
      </div>

      {errorMessages.length > 0 && <ErrorsAlert errors={errorMessages} />}

      {priceQuoteReq && !isPendingPrices && (!priceQuote || !priceQuote.categories?.length) && (
        <div className="reserve-section">
          <div style={{ padding: "1rem", backgroundColor: "#fff3f3", borderRadius: "4px", marginBottom: "1rem" }}>
            <h4>No prices available for selected search.</h4>
            {priceQuoteReq?.departureStation !== priceQuoteReq?.returnStation && (
              <h4>Transfer may not be available.</h4>
            )}
          </div>
        </div>
      )}

      {priceQuote && priceQuote.categories?.length > 0 && (
        <div className="reserve-section">
          <h2>Car Selection</h2>
          <Table>
            <tbody>
              {priceQuote.categories.map((cat) => (
                <tr key={cat.code} 
                    style={{ 
                      fontWeight: vehicle?.code === cat.code ? "bolder" : "normal",
                      cursor: "pointer",
                      backgroundColor: vehicle?.code === cat.code ? "rgb(192 221 255)" : "transparent"
                    }} 
                    onClick={()=>setVehicle(cat)}>
                  <td>{cat.model}</td>
                  <td style={{width: "15rem", textAlign: "right"}}>{formatCurrency(cat.totalRate)} {priceQuote.currency}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {priceQuote && vehicle && (
        <>
          <div className="reserve-section">
            <h2>Charge Summary</h2>
            <div style={{ marginBottom: "1.5rem" }}>
              <p>The following items and services are included in your rental without additional fees:</p>
              <ul style={{ paddingLeft: "2rem", color: "#666" }}>
                <li>TC Basic collision damage waiver</li>
                <li>Unlimited rental kilometers</li>
                <li>Navigation</li>
                <li>24/7 Road Side Assistance service</li>
              </ul>
            </div>
            {extras &&
              <Extras
                extras={extras}
                setExtras={setExtras}
                currency={priceQuote.currency}
                ref={extrasRef}
              />
            }
          </div>

          <div className="reserve-section">
            <h2>Driver Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {createInput(EInputs.customerFirstName)}
              {createInput(EInputs.customerLastName)}
              {createInput(EInputs.customerPhone)}
              {createInput(EInputs.customerEmail)}
              {createInput(EInputs.customerAddress)}
              {createInput(EInputs.customerCountry)}
              {createInput(EInputs.customerCity)}
              {createInput(EInputs.customerZip)}
            </div>
            <div style={{ marginTop: "1rem" }}>
              {createInput(EInputs.meetingPoint)}
              
              {inputs[EInputs.meetingPoint].value === EMeetingPoint.AIRPORT && (
                <>
                  {createInput(EInputs.meetingPointAirport)}
                  
                  {inputs[EInputs.meetingPointAirport].value === EMeetingPointAirport.FLIGHT && (
                    <>
                      {createInput(EInputs.meetingPointFlightNumber)}
                      {createInput(EInputs.meetingPointEta)}
                    </>
                  )}
                  
                  {inputs[EInputs.meetingPointAirport].value === EMeetingPointAirport.NO_FLIGHT && (
                    <>
                      {createInput(EInputs.meetingPointTerminal)}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="reserve-section">
            <h2>Total number of passengers</h2>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
              <div className="w-full sm:w-32">{createInput(EInputs.adults)}</div>
              <div className="w-full sm:w-32">{createInput(EInputs.childs)}</div>
              <div className="w-full sm:w-32">{createInput(EInputs.infants)}</div>
            </div>
          </div>

          
          {
          babySeatGroups.length > 0 && (
            <>
              <h2>Children requiring baby seats</h2>
              
              {babySeatGroups.map((group) => (
                Array.from({ length: group.count }).map((_, index) => (
                  <div key={`${group.type}-${index}`} className="w-full sm:w-40 mb-4 sm:mb-0">
                    <div><b>{group.label} {index + 1}</b></div>
                    
                    <div>
                      <Input
                        type={EInputType.text}
                        inputName={"Age "+index}
                        label="Children's age"
                        placeholder="Age"
                        value={babySeatsData[`${group.type}_age_${index}`] || ''}
                        required={true}
                        onChange={(value) => handleInputChangeBabySeats(`${group.type}_age_${index}`, value?.toString())}
                        onBlurModifyValue={(val) => Math.max(0, parseInt(val as string) || 0).toString()}
                      />
                    </div>
                    
                    <div>
                      <Input
                        type={EInputType.text}
                        inputName={"Height "+index}
                        label="Height, in centimeters"
                        placeholder="Height"
                        value={babySeatsData[`${group.type}_height_${index}`] || ''}
                        required={true}
                        onChange={(value) => handleInputChangeBabySeats(`${group.type}_height_${index}`, value?.toString())}
                        onBlurModifyValue={(val) => Math.max(0, parseInt(val as string) || 0).toString()}
                      />
                    </div>
                  </div>
                ))
              ))}
            </>
          )}

          <div className="reserve-section">
            <h2>Special requests</h2>
            {createInput(EInputs.notes)}
          </div>

          <div className="reserve-section">
            <h2>Custom reference number</h2>
            {createInput(EInputs.refNo)}
            {isValidatingRefNo && <div>Validating reference number...</div>}
            {refNoMessage && <div style={{ color: refNoMessage.toLowerCase().includes("valid") ? "green" : "red" }}>{refNoMessage}</div>}
          </div>


          <div className="reserve-section">
            <h2>Summary</h2>
            <Table>
              <tbody>
                {shoppingBasket.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td style={{ textAlign: "right" }}>{formatCurrency(item.value)} {priceQuote.currency}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                  <td style={{ borderTop: "2px solid #eee", paddingTop: "1rem" }}>Total</td>
                  <td style={{ borderTop: "2px solid #eee", paddingTop: "1rem", textAlign: "right" }}>
                    {formatCurrency(shoppingBasket.totalRate)} {priceQuote.currency}
                  </td>
                </tr>
              </tfoot>
            </Table>

            <div style={{ marginTop: "2rem", textAlign: "right" }}>
              <Button 
                onClick={submitHandlerReserve} 
                color={EButtonColor.SUCCESS} 
                size={EButtonSize.LARGE} 
                style={{ minWidth: "15rem" }}
                loading={isPendingSave}
                disabled={isPendingSave}
              >
                Finish Booking
              </Button>
              {submitError && <div style={{ color: "red", marginTop: "1rem" }}>{submitError}</div>}
              {isErrorSave && errorSave && <div style={{ color: "red", marginTop: "1rem" }}>{errorSave.messages ? errorSave.messages.join(", ") : errorSave.message}</div>}
            </div>
          </div>
        </>
      )}
    </>
  );
};

interface ITab {
  id: string;
  label: string;
  summary?: ITabSummary;
  initialData?: IPriceQuoteRequest;
  externalData?: { data: IPriceQuoteRequest; triggerId: number };
}

const LOCAL_STORAGE_KEY = "tc_previous_searches";

const Reserve: React.FC = () => {
  const navigate = useNavigate();
  const { pickupOptions, returnOptions, loading: stationsLoading, stations } = useStationOptions();
  const tabCounterRef = useRef<number>(1);
  const initialTabIdRef = useRef<string>(`tab_${Date.now()}_1`);
  const [tabs, setTabs] = useState<ITab[]>([{ id: initialTabIdRef.current, label: "Search 1" }]);
  const [activeTabId, setActiveTabId] = useState<string>(initialTabIdRef.current);
  const MAX_TABS = 7;

  const [savedSearches, setSavedSearches] = useState<ISavedSearch[]>(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error("Failed to parse previous searches from localStorage", e);
    }
    return [];
  });

  const tabsSearchState = useRef<Record<string, IPriceQuoteRequest>>({});
  const tabCurrentSearchIdRef = useRef<Record<string, string>>({});

  const handleUpdateSearchState = useCallback((id: string, data: IPriceQuoteRequest) => {
    tabsSearchState.current[id] = data;
  }, []);

  const handleUpdateSummary = useCallback((id: string, summary: ITabSummary) => {
    setTabs(prev => prev.map(t => t.id === id ? { ...t, summary } : t));
  }, []);

  const handleSearchExecuted = useCallback((tabId: string, searchData: IPriceQuoteRequest) => {
    const depStation = stations?.find(s => s.code === searchData.departureStation);
    const retStation = stations?.find(s => s.code === searchData.returnStation);

    const depDateParsed = parseISO(searchData.departureDate);
    const retDateParsed = parseISO(searchData.returnDate);
    const duration = differenceInDays(retDateParsed, depDateParsed) + 1;

    setSavedSearches(prevSearches => {
      const activeSearchId = tabCurrentSearchIdRef.current[tabId];

      // Check if this tab is already tracking a history entry
      const existingIndex = activeSearchId 
        ? prevSearches.findIndex(s => s.id === activeSearchId)
        : -1;

      const entryId = existingIndex >= 0 
        ? prevSearches[existingIndex].id 
        : `search_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

      // Link tab to this search ID so future searches in this tab update this single entry
      tabCurrentSearchIdRef.current[tabId] = entryId;

      const updatedEntry: ISavedSearch = {
        id: entryId,
        tabId: tabId,
        searchData: { ...searchData },
        departureStationCode: searchData.departureStation,
        returnStationCode: searchData.returnStation,
        departureStationName: depStation?.name || searchData.departureStation,
        returnStationName: retStation?.name || searchData.returnStation,
        departureDate: searchData.departureDate,
        returnDate: searchData.returnDate,
        durationDays: duration > 0 ? duration : 1,
        timestamp: Date.now(),
      };

      let updatedList: ISavedSearch[];
      if (existingIndex >= 0) {
        // Move the updated search to the top of the list
        const withoutExisting = prevSearches.filter((_, idx) => idx !== existingIndex);
        updatedList = [updatedEntry, ...withoutExisting];
      } else {
        // First search in this new tab -> add one entry
        updatedList = [updatedEntry, ...prevSearches];
      }

      if (updatedList.length > 30) {
        updatedList = updatedList.slice(0, 30);
      }

      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedList));
      } catch (e) {
        console.error("Failed to save previous searches to localStorage", e);
      }

      return updatedList;
    });
  }, [stations]);

  const handleSelectSearch = useCallback((search: ISavedSearch) => {
    tabCurrentSearchIdRef.current[activeTabId] = search.id;

    const depStation = stations?.find(s => s.code === search.searchData.departureStation);
    const pickupFirstWord = depStation?.name.split(' ')[0] || search.departureStationCode;
    const depDateParsed = parseISO(search.searchData.departureDate);
    const retDateParsed = parseISO(search.searchData.returnDate);

    let summary: ITabSummary | undefined = undefined;
    if (isValid(depDateParsed) && isValid(retDateParsed)) {
      summary = {
        pickup: pickupFirstWord,
        dates: `${format(depDateParsed, "dd/MM")} - ${format(retDateParsed, "dd/MM")}`
      };
    }

    setTabs(prev => prev.map(tab => {
      if (tab.id === activeTabId) {
        return {
          ...tab,
          summary: summary || tab.summary,
          externalData: {
            data: { ...search.searchData },
            triggerId: Date.now()
          }
        };
      }
      return tab;
    }));
  }, [activeTabId, stations]);

  const handleOpenInNewTab = useCallback((search: ISavedSearch) => {
    if (tabs.length >= MAX_TABS) return;

    tabCounterRef.current += 1;
    const nextId = `tab_${Date.now()}_${tabCounterRef.current}`;
    delete tabCurrentSearchIdRef.current[nextId];

    const depStation = stations?.find(s => s.code === search.searchData.departureStation);
    const pickupFirstWord = depStation?.name.split(' ')[0] || search.departureStationCode;
    const depDateParsed = parseISO(search.searchData.departureDate);
    const retDateParsed = parseISO(search.searchData.returnDate);

    let summary: ITabSummary | undefined = undefined;
    if (isValid(depDateParsed) && isValid(retDateParsed)) {
      summary = {
        pickup: pickupFirstWord,
        dates: `${format(depDateParsed, "dd/MM")} - ${format(retDateParsed, "dd/MM")}`
      };
    }

    setTabs(prev => [
      ...prev,
      {
        id: nextId,
        label: `Search ${tabCounterRef.current}`,
        summary,
        initialData: { ...search.searchData }
      }
    ]);
    setActiveTabId(nextId);
  }, [tabs.length, stations]);

  const handleDeleteSearch = useCallback((id: string) => {
    Object.keys(tabCurrentSearchIdRef.current).forEach(tabId => {
      if (tabCurrentSearchIdRef.current[tabId] === id) {
        delete tabCurrentSearchIdRef.current[tabId];
      }
    });

    setSavedSearches(prev => {
      const next = prev.filter(s => s.id !== id);
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        console.error("Failed to update localStorage", e);
      }
      return next;
    });
  }, []);

  const handleClearAll = useCallback(() => {
    tabCurrentSearchIdRef.current = {};
    setSavedSearches([]);
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch (e) {
      console.error("Failed to clear localStorage", e);
    }
  }, []);

  const handleBookingSuccess = useCallback((refNo: string, irn: string | undefined, completedTabId: string) => {
    const targetUrl = Route.reservation(refNo, irn);
    let popupBlocked = false;

    try {
      const newWindow = window.open(targetUrl, '_blank');
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        popupBlocked = true;
      }
    } catch (e) {
      popupBlocked = true;
    }

    if (popupBlocked) {
      navigate(targetUrl);
      return;
    }

    delete tabsSearchState.current[completedTabId];
    delete tabCurrentSearchIdRef.current[completedTabId];

    setTabs(prevTabs => {
      const remainingTabs = prevTabs.filter(t => t.id !== completedTabId);

      if (remainingTabs.length === 0) {
        tabCounterRef.current += 1;
        const newTabId = `tab_${Date.now()}_${tabCounterRef.current}`;
        setActiveTabId(newTabId);
        return [{ id: newTabId, label: `Search ${tabCounterRef.current}` }];
      }

      if (activeTabId === completedTabId) {
        setActiveTabId(remainingTabs[0].id);
      }

      return remainingTabs;
    });
  }, [activeTabId, navigate]);

  const addTab = () => {
    if (tabs.length >= MAX_TABS) return;
    tabCounterRef.current += 1;
    const nextId = `tab_${Date.now()}_${tabCounterRef.current}`;
    delete tabCurrentSearchIdRef.current[nextId];
    setTabs(prev => [...prev, { 
      id: nextId, 
      label: `Search ${tabCounterRef.current}`
    }]);
    setActiveTabId(nextId);
  };

  const removeTab = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (tabs.length === 1) return;
    delete tabsSearchState.current[id];
    delete tabCurrentSearchIdRef.current[id];
    const newTabs = tabs.filter(t => t.id !== id);
    setTabs(newTabs);
    if (activeTabId === id) {
      setActiveTabId(newTabs[newTabs.length - 1].id);
    }
  };

  return (
    <div className="reserve-page">
      <div className="tab-bar">
        {tabs.map(tab => (
          <div 
            key={tab.id} 
            className={`tab-item ${activeTabId === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTabId(tab.id)}
          >
            {tab.summary ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", fontSize: "0.85rem", lineHeight: "1.2" }}>
                <div style={{ fontWeight: "bold" }}>{tab.summary.pickup}</div>
                <div style={{ opacity: 0.7 }}>{tab.summary.dates}</div>
              </div>
            ) : tab.label}
            {tabs.length > 1 && (
              <span className="remove-tab" onClick={(e) => removeTab(e, tab.id)}>&times;</span>
            )}
          </div>
        ))}
        {tabs.length < MAX_TABS && <button className="add-tab-btn" onClick={addTab}>+</button>}
      </div>

      {tabs.map(tab => (
        <div key={tab.id} className={`tab-content ${activeTabId === tab.id ? '' : 'hidden'}`}>
          <ReserveTabContent 
            tabId={tab.id}
            pickupOptions={pickupOptions}
            returnOptions={returnOptions}
            stationsLoading={stationsLoading}
            stations={stations}
            onUpdateSummary={handleUpdateSummary}
            onUpdateSearchState={handleUpdateSearchState}
            onSearchExecuted={handleSearchExecuted}
            onBookingSuccess={(refNo, irn) => handleBookingSuccess(refNo, irn, tab.id)}
            initialData={tab.initialData}
            externalData={tab.externalData}
            savedSearches={savedSearches}
            onSelectSearch={handleSelectSearch}
            onOpenInNewTab={handleOpenInNewTab}
            onDeleteSearch={handleDeleteSearch}
            onClearAll={handleClearAll}
          />
        </div>
      ))}
    </div>
  );
};

const usePriceQuoteInputs = (minDeparture: string, minReturn: string, returnOptions: IOption[] | undefined, data?: IPriceQuoteRequest) => {
  const [inputs, setInputs] = useState<IInputField>({
    [EInputsPriceQuote.departureDate]: {
      type: EInputType.date,
      label: "Pickup Date",
      value: data?.departureDate || minDeparture,
      validation: {
        required: true,
      },
      min: minDeparture,
    },
    [EInputsPriceQuote.departureTime]: {
      type: EInputType.reactSelect,
      label: "Pickup Time",
      value: data?.departureTime || "15:00",
      options: timeOptions,
      validation: {
        required: true,
      },
    },
    [EInputsPriceQuote.departureStation]: {
      type: EInputType.reactSelect,
      label: "Pickup Location",
      value: data?.departureStation || "HEL",
      validation: {
        required: true,
      },
    },
    [EInputsPriceQuote.returnDate]: {
      type: EInputType.date,
      label: "Return Date",
      value: data?.returnDate || minReturn,
      validation: {
        required: true,
      },
      min: minReturn,
    },
    [EInputsPriceQuote.returnTime]: {
      type: EInputType.reactSelect,
      label: "Return Time",
      value: data?.returnTime || "12:00",
      options: timeOptions,
      validation: {
        required: true,
      },
    },
    [EInputsPriceQuote.returnStation]: {
      type: EInputType.reactSelect,
      label: "Return Location",
      value: data?.returnStation || "HEL",
      validation: {
        required: true,
      },
    },
    [EInputsPriceQuote.discountCode]: {
      type: EInputType.text,
      label: "Discount Coupon:",
      value: data?.discountCode || "",
    },
  });

  const { createInput, submit } = useInputs({ data, inputs, setInputs });

  const departureDateValue = inputs[EInputsPriceQuote.departureDate].value;
  const departureStationValue = inputs[EInputsPriceQuote.departureStation].value;

  const previousDepartureRef = useRef<string>(departureDateValue as string);
  const isSettingFormValuesRef = useRef<boolean>(false);
  const [viewYearMonth, setViewYearMonth] = useState<string | null>(null);

  useEffect(() => {
    if (departureDateValue) {
      setViewYearMonth(format(new Date(departureDateValue as string), 'yyyy-MM'));
    }
  }, [departureDateValue]);

  const yearMonth = viewYearMonth || (departureDateValue ? format(new Date(departureDateValue as string), 'yyyy-MM') : format(new Date(), 'yyyy-MM'));
  const currentStation = departureStationValue ? departureStationValue as string : "HEL";

  const { data: availabilityData } = useStationAvailability(currentStation, yearMonth);

  // Proactive fetch for next month so it's ready if user browses forward
  const nextMonth = format(addMonths(parseISO(yearMonth + '-01'), 1), 'yyyy-MM');
  useStationAvailability(currentStation, nextMonth);

  const dayClassName = useCallback((date: Date) => {
    const dayKey = format(date, 'yyyy-MM-dd');
    if (availabilityData?.data && availabilityData.data[dayKey]) {
      const availableCount = availabilityData.data[dayKey].availableCount;
      if (availableCount === 0) return "react-datepicker__day--no-availability";
      if (availableCount <= 2) return "react-datepicker__day--some-availability";
    }
    return "";
  }, [availabilityData]);

  useEffect(() => {
    if (isSettingFormValuesRef.current) {
      isSettingFormValuesRef.current = false;
      previousDepartureRef.current = departureDateValue as string;
      return;
    }

    if (departureDateValue) {
      const parsedDep = parseISO(departureDateValue as string);
      
      if (!isValid(parsedDep)) {
        return;
      }

      const nextMinReturn = format(addDays(parsedDep, 1), "yyyy-MM-dd");
      const prevDeparture = previousDepartureRef.current;

      let daysShifted = 0;
      if (prevDeparture && prevDeparture !== departureDateValue) {
        const parsedPrevDep = parseISO(prevDeparture);
        if (isValid(parsedPrevDep)) {
          daysShifted = differenceInDays(parsedDep, parsedPrevDep);
        }
      }

      setInputs(prev => {
        const currentReturnDate = prev[EInputsPriceQuote.returnDate].value as string;
        let newReturnDate = currentReturnDate;

        if (daysShifted !== 0 && currentReturnDate) {
          const parsedRet = parseISO(currentReturnDate);
          if (isValid(parsedRet)) {
            newReturnDate = format(addDays(parsedRet, daysShifted), "yyyy-MM-dd");
          }
        }
        
        if (newReturnDate < nextMinReturn) {
          newReturnDate = nextMinReturn;
        }

        const needsUpdate = prev[EInputsPriceQuote.returnDate].min !== nextMinReturn || prev[EInputsPriceQuote.returnDate].value !== newReturnDate;

        if (needsUpdate) {
          return {
            ...prev,
            [EInputsPriceQuote.returnDate]: {
              ...prev[EInputsPriceQuote.returnDate],
              min: nextMinReturn,
              value: newReturnDate
            }
          };
        }
        return prev;
      });

      previousDepartureRef.current = departureDateValue as string;
    }
  }, [departureDateValue, setInputs]);

  useEffect(() => {
    if (isSettingFormValuesRef.current) {
      return;
    }
    if (departureStationValue) {
      setInputs(prev => {
        const currentReturnStation = prev[EInputsPriceQuote.returnStation].value as string;
        const existsInReturn = returnOptions?.some(opt => opt.value === departureStationValue);
        
        if (existsInReturn && currentReturnStation !== departureStationValue) {
          return {
            ...prev,
            [EInputsPriceQuote.returnStation]: {
              ...prev[EInputsPriceQuote.returnStation],
              value: departureStationValue
            }
          };
        }
        return prev;
      });
    }
  }, [departureStationValue, returnOptions, setInputs]);

  const setFormValues = useCallback((newData: IPriceQuoteRequest) => {
    isSettingFormValuesRef.current = true;
    previousDepartureRef.current = newData.departureDate;

    const parsedDep = parseISO(newData.departureDate);
    const nextMinReturn = isValid(parsedDep) ? format(addDays(parsedDep, 1), "yyyy-MM-dd") : minReturn;

    setInputs(prev => ({
      ...prev,
      [EInputsPriceQuote.departureDate]: {
        ...prev[EInputsPriceQuote.departureDate],
        value: newData.departureDate,
      },
      [EInputsPriceQuote.departureTime]: {
        ...prev[EInputsPriceQuote.departureTime],
        value: newData.departureTime,
      },
      [EInputsPriceQuote.departureStation]: {
        ...prev[EInputsPriceQuote.departureStation],
        value: newData.departureStation,
      },
      [EInputsPriceQuote.returnDate]: {
        ...prev[EInputsPriceQuote.returnDate],
        min: nextMinReturn,
        value: newData.returnDate,
      },
      [EInputsPriceQuote.returnTime]: {
        ...prev[EInputsPriceQuote.returnTime],
        value: newData.returnTime,
      },
      [EInputsPriceQuote.returnStation]: {
        ...prev[EInputsPriceQuote.returnStation],
        value: newData.returnStation,
      },
      [EInputsPriceQuote.discountCode]: {
        ...prev[EInputsPriceQuote.discountCode],
        value: newData.discountCode || "",
      },
    }));
  }, [minReturn]);

  return {
    createInput: (key: string, options?: any) => createInput(key, {
      ...options,
      dayClassName: (key === EInputsPriceQuote.departureDate || key === EInputsPriceQuote.returnDate) ? dayClassName : undefined,
      onMonthChange: (key === EInputsPriceQuote.departureDate || key === EInputsPriceQuote.returnDate) ? (date: Date) => setViewYearMonth(format(date, 'yyyy-MM')) : undefined
    }),
    submit,
    inputs,
    setInputs,
    setFormValues
  };
};

const useDriverDetailsInputs = (data?: IPriceQuoteRequest) => {
  const [inputs, setInputs] = useState<IInputField>({
    [EInputs.customerFirstName]: {
      type: EInputType.text,
      label: "Driver First Name",
      placeholder: "Driver First Name",
      value: "",
      validation: { required: true },
    },
    [EInputs.customerLastName]: {
      type: EInputType.text,
      label: "Last Name",
      placeholder: "Last Name",
      value: "",
      validation: { required: true },
    },
    [EInputs.customerPhone]: {
      type: EInputType.text,
      label: "Driver Telephone",
      placeholder: "Driver Telephone",
      value: "",
      validation: { required: true },
    },
    [EInputs.customerEmail]: {
      type: EInputType.email,
      label: "Driver E-mail",
      placeholder: "Driver E-mail",
      value: "",
      validation: { required: true },
    },
    [EInputs.customerAddress]: {
      type: EInputType.text,
      label: "Driver Address",
      placeholder: "Driver Address",
      value: "",
    },
    [EInputs.customerCity]: {
      type: EInputType.text,
      label: "Driver City",
      placeholder: "Driver City",
      value: "",
    },
    [EInputs.customerZip]: {
      type: EInputType.text,
      label: "Driver Zip",
      placeholder: "Driver Zip",
      value: "",
    },
    [EInputs.customerCountry]: {
      type: EInputType.reactSelect,
      label: "Driver Country",
      placeholder: "Select country",
      value: "",
      options: countryOptions
    },
    [EInputs.meetingPoint]: {
      type: EInputType.radio,
      label: "Meeting point",
      value: "",
      options: meetingPointOptions,
      validation: {
        dependencies: [EInputs.meetingPointAirport]
      }
    },
    [EInputs.meetingPointAirport]: {
      type: EInputType.radio,
      label: "Airport info",
      value: "",
      options: meetingPointAirportOptions,
      validation: {
        requiredIf: EInputs.meetingPoint,
        requiredIfValue: EMeetingPoint.AIRPORT,
        dependencies: [EInputs.meetingPointFlightNumber, EInputs.meetingPointEta, EInputs.meetingPointTerminal]
      }
    },
    [EInputs.meetingPointFlightNumber]: {
      type: EInputType.text,
      label: "Flight number",
      value: "",
      validation: { 
        maxLength: 10,
        requiredIf: EInputs.meetingPointAirport,
        requiredIfValue: EMeetingPointAirport.FLIGHT
      },
    },
    [EInputs.meetingPointEta]: {
      type: EInputType.reactSelect,
      label: "ETA",
      value: "",
      options: timeOptions,
      validation: { 
        requiredIf: EInputs.meetingPointAirport, 
        requiredIfValue: EMeetingPointAirport.FLIGHT 
      },
    },
    [EInputs.meetingPointTerminal]: {
      type: EInputType.reactSelect,
      label: "Terminal",
      placeholder: "Select terminal",
      value: "",
      options: terminalOptions,
      validation: {
        requiredIf: EInputs.meetingPointAirport,
        requiredIfValue: EMeetingPointAirport.NO_FLIGHT
      }
    },
    [EInputs.adults]: {
      type: EInputType.number,
      label: "Adults",
      value: "",
      validation: { 
        required: true, 
        minAmount: 1
      },
      onBlurModifyValue: (val) => Math.max(1, parseInt(val as string) || 1).toString()
    },
    [EInputs.childs]: {
      type: EInputType.number,
      label: "Children",
      value: "",
      validation: { 
        required: true,
        minAmount: 0
      },
      onBlurModifyValue: (val) => Math.max(0, parseInt(val as string) || 0).toString()
    },
    [EInputs.infants]: {
      type: EInputType.number,
      label: "Infants",
      value: "",
      validation: { 
        required: true,
        minAmount: 0
      },
      onBlurModifyValue: (val) => Math.max(0, parseInt(val as string) || 0).toString()
    },
    [EInputs.notes]: {
      type: EInputType.text,
      value: "",
      placeholder: "Special requests",
      validation: {
        maxLength: 100
      },
    },
    [EInputs.refNo]: {
      type: EInputType.text,
      value: "",
      placeholder: "example HEL90935456612A"
    },
  });

  const { createInput, submit } = useInputs({ data, inputs, setInputs });
  
  return { createInput, submit, inputs };
};

const useSearch = (setPriceQuote: Dispatch<SetStateAction<IPriceQuote | undefined>>) => {
  const { fetchPrices } = useBookingService();

  const {
    mutate: getPriceQuote,
    isPending: isPendingPrices,
    isError: isErrorPrices,
    error: errorPrices,
  } = useMutation<IPriceQuote, ErrorResponse, IPriceQuoteRequest>({
    mutationFn: fetchPrices,
    onSuccess: (data) => {
      setPriceQuote(data);
    },
  });

  return { getPriceQuote, isPendingPrices, isErrorPrices, errorPrices };
};

const useSaveReservation = () => {
  const { saveReservation } = useReservationService();

  const {
    mutateAsync: saveReservationAsync,
    isPending: isPendingSave,
    isError: isErrorSave,
    error: errorSave,
  } = useMutation<IReservation, ErrorResponse, IReserve>({
    mutationFn: saveReservation,
  });

  return { saveReservationAsync, isPendingSave, isErrorSave, errorSave };
};

export default Reserve;
